import "@components/ag-grid/styles/AgGrid.css";
import { ColDef, ColGroupDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";
import { clsxm } from "../../utils/clxm/clxm";
import { enrichAgGridColumnDefs } from "./enrichColumnDefsForNumericLayout";

interface AgGridSkeletonProps {
  columnDefs: ColDef[];
  defaultColDef?: any;
  rowCount?: number;
  className?: string;
  style?: React.CSSProperties;
  centerAlignHeaders?: boolean;
}

const AgGridSkeleton: React.FC<AgGridSkeletonProps> = ({
  columnDefs,
  defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressSizeToFit: false,
    floatingFilter: true,
  },
  rowCount = 10,
  className = "",
  style = {},
  centerAlignHeaders = false,
}) => {
  const columnDefsForLayout = useMemo(
    () =>
      enrichAgGridColumnDefs(
        columnDefs as (ColDef | ColGroupDef)[],
      ) as ColDef[],
    [columnDefs],
  );

  const generateSkeletonData = () => {
    return Array.from({ length: rowCount }, (_, index) => {
      const skeletonRow: any = { id: `skeleton-${index}` };

      columnDefsForLayout.forEach((col) => {
        if (col.field) {
          skeletonRow[col.field] = "";
        }
      });

      skeletonRow._isSkeleton = true;
      return skeletonRow;
    });
  };

  const skeletonColumnDefs: ColDef[] = columnDefsForLayout.map((col) => ({
    ...col,
    cellRenderer: (params: any) => {
      if (params.data?._isSkeleton) {
        let width = 80;
        if (col.field?.toLowerCase().includes("id")) width = 60;
        if (col.field?.toLowerCase().includes("date")) width = 70;
        if (col.field?.toLowerCase().includes("amount")) width = 90;
        if (col.field?.toLowerCase().includes("type")) width = 100;
        if (col.field?.toLowerCase().includes("merchant")) width = 120;
        if (col.field?.toLowerCase().includes("network")) width = 60;
        const randomWidth = width + Math.random() * 20 - 10;
        return <Skeleton height={12} width={randomWidth} />;
      }

      if (col.cellRenderer && typeof col.cellRenderer === "function") {
        return col.cellRenderer(params);
      }

      return params.valueFormatted || params.value;
    },
    sortable: false,
    filter: false,
    floatingFilter: true,
  }));

  const skeletonData = generateSkeletonData();

  return (
    <div
      className={clsxm(
        "ag-theme-alpine",
        className,
        centerAlignHeaders && "ag-grid-center-headers",
        "rounded-none",
      )}
      style={style}
    >
      <AgGridReact
        theme="legacy"
        rowData={skeletonData}
        columnDefs={skeletonColumnDefs}
        defaultColDef={defaultColDef}
        headerHeight={40}
        rowHeight={48}
        suppressCellFocus={true}
        suppressMovableColumns={true}
        suppressColumnMoveAnimation={true}
        animateRows={false}
      />
    </div>
  );
};

export default AgGridSkeleton;
